import Reveal from './ui/Reveal';
import Icon, { GithubIcon } from './ui/Icon';
import Button from './ui/Button';
import SectionHeading from './ui/SectionHeading';
import { projects, site } from '../data/content';

const host = (url) => url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

const ProjectImage = ({ project, priority = false, sizes }) => (
  <a
    href={project.live}
    target="_blank"
    rel="noopener noreferrer"
    tabIndex={-1}
    aria-hidden="true"
    className="block overflow-hidden rounded-xl border border-white/10 bg-ink-800"
  >
    <div className="flex h-8 items-center gap-1.5 border-b border-white/[0.08] bg-ink-700/70 px-3.5">
      <span className="h-2 w-2 rounded-full bg-white/15" />
      <span className="h-2 w-2 rounded-full bg-white/15" />
      <span className="h-2 w-2 rounded-full bg-white/15" />
      <span className="ml-2 min-w-0 flex-1 truncate rounded bg-black/30 px-2.5 py-0.5 font-mono text-[10.5px] text-subtle">
        {host(project.live)}
      </span>
    </div>
    <img
      src={project.image.src1280}
      srcSet={`${project.image.src720} 720w, ${project.image.src1280} 1280w`}
      sizes={sizes}
      width="1280"
      height="800"
      alt={project.alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      className="block h-auto w-full transition-transform duration-500 group-hover:scale-[1.015]"
    />
  </a>
);

const ProjectLinks = ({ project }) => (
  <div className="flex flex-wrap items-center gap-3">
    <Button href={project.live} external arrow variant="secondary" aria-label={`Visit ${project.name} live site (opens in a new tab)`}>
      Live site
    </Button>
    {project.github && (
      <Button
        href={project.github}
        external
        variant="secondary"
        aria-label={`View ${project.name} source code on GitHub (opens in a new tab)`}
      >
        <GithubIcon className="h-4 w-4" />
        Source code
      </Button>
    )}
  </div>
);

const TechList = ({ tech }) => (
  <ul className="flex flex-wrap gap-2" aria-label="Technology used">
    {tech.map((t) => (
      <li key={t} className="pill">
        {t}
      </li>
    ))}
  </ul>
);

const FeatureList = ({ features }) => (
  <ul className="space-y-2">
    {features.map((f) => (
      <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed text-fg/90">
        <Icon name="Check" className="mt-[3px] h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} />
        {f}
      </li>
    ))}
  </ul>
);

const Problem = ({ text }) => (
  <p className="text-sm leading-relaxed text-muted">
    <span className="font-semibold text-fg">The problem: </span>
    {text}
  </p>
);

const FeaturedProject = ({ project }) => (
  <Reveal>
    <article
      className="card group grid gap-8 p-5 sm:p-7 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-12 lg:p-9"
      aria-labelledby={`${project.slug}-title`}
    >
      <ProjectImage project={project} priority sizes="(min-width: 1024px) 560px, (min-width: 640px) 90vw, 92vw" />
      <div>
        <p className="eyebrow">Featured · {project.category}</p>
        <h3 id={`${project.slug}-title`} className="mt-3 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
          {project.name}
        </h3>
        <p className="mt-3 text-base leading-relaxed text-muted">{project.summary}</p>
        <div className="mt-5">
          <Problem text={project.problem} />
        </div>
        <div className="mt-5">
          <FeatureList features={project.features} />
        </div>
        <div className="mt-6">
          <TechList tech={project.tech} />
        </div>
        <div className="mt-7">
          <ProjectLinks project={project} />
        </div>
      </div>
    </article>
  </Reveal>
);

const ProjectCard = ({ project, index }) => (
  <Reveal as="li" delay={(index % 2) * 80} className="flex min-w-0">
    <article
      className="card card-hover group flex w-full flex-col p-5 sm:p-6"
      aria-labelledby={`${project.slug}-title`}
    >
      <ProjectImage project={project} sizes="(min-width: 1024px) 540px, (min-width: 640px) 45vw, 90vw" />
      <div className="mt-6 flex flex-1 flex-col">
        <p className="eyebrow">{project.category}</p>
        <h3 id={`${project.slug}-title`} className="mt-2.5 text-xl font-semibold tracking-tight text-fg">
          {project.name}
        </h3>
        <p className="mt-2.5 text-[15px] leading-relaxed text-muted">{project.summary}</p>
        <div className="mt-4">
          <Problem text={project.problem} />
        </div>
        <div className="mt-4">
          <FeatureList features={project.features} />
        </div>
        <div className="mt-5">
          <TechList tech={project.tech} />
        </div>
        <div className="mt-auto pt-6">
          <ProjectLinks project={project} />
        </div>
      </div>
    </article>
  </Reveal>
);

const Projects = () => {
  const [featured, ...rest] = projects;

  return (
    <section id="work" className="section border-t border-white/[0.06]" aria-labelledby="work-title">
      <div className="container-page">
        <SectionHeading
          id="work-title"
          eyebrow="Selected work"
          title="Real projects, live on the web"
          lead="A selection of websites and web apps I’ve built. Each one is live, so you can click through and see how it works."
        />

        <div className="mt-12 space-y-5 lg:space-y-6">
          <FeaturedProject project={featured} />
          <ul className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
            {rest.map((project, i) => (
              <ProjectCard key={project.slug} project={project} index={i} />
            ))}
          </ul>
        </div>

        <Reveal className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted">More code and experiments are available on GitHub.</p>
          <Button href={site.github} external variant="secondary">
            <GithubIcon className="h-4 w-4" />
            Browse GitHub
          </Button>
        </Reveal>
      </div>
    </section>
  );
};

export default Projects;
